import type { FC } from "react";
import { useRef } from "react";

import styled from "styled-components";

import { ChipDownContainer } from "@similarweb/ui-components";

import { allTrackers } from "app/services/track/track";
import type { IRangeFilterProps, IRangeValues } from "pro-features/components/filtersPanel/src/RangeFilter";
import { RangeFilter } from "pro-features/components/filtersPanel/src/RangeFilter";
import {
    getVolumeSelectedText,
    isValidVolumeRangeValues,
    onContainerKeyDown,
} from "pro-features/components/filtersPanel/src/RangeFilterUtilityFunctions";
import { PlainTooltip } from "app/components/React/Tooltip/PlainTooltip/PlainTooltip";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

const VOLUME_FILTER_WIDTH = 298;
const VOLUME_FILTER_BUTTON_TEXT_KEY = "digitalsuite.backlinks.overview.dts_widget.title";
const VOLUME_FILTER_DISABLED_TOOLTIP_KEY = "digitalsuite.backlinks.table.column.domain_inlink_rank.tooltip";

const ChipDownContainerWrapper = styled.div.attrs({
    id: "dts-filter",
})`
    position: relative;
`;

// This is the representational component
export const DTSFilter: FC<{
    onChange?: (rangeValues: IRangeValues) => void;
    tableFilters?: IRangeValues;
    tooltipText?: string;
    filterTextKey?: string;
    rangeFilterProps?: Omit<
        IRangeFilterProps,
        "onDoneCallback" | "initialFromValue" | "initialToValue" | "isValidRangeValues" | "onClearCallback"
    >;
    minFromValue?: number;
    maxFromValue?: number;
    minToValue?: number;
    maxToValue?: number;
    enabledTooltip?: boolean;
    selectedText?: { VOLUME: string; AT_LEAST: string; UP_TO: string };
}> = ({
    onChange,
    tableFilters = { fromValue: "", toValue: "" },
    tooltipText,
    filterTextKey = VOLUME_FILTER_BUTTON_TEXT_KEY,
    rangeFilterProps = {},
    enabledTooltip = false,
    selectedText = { VOLUME: "DTS", AT_LEAST: "DTS >", UP_TO: "DTS <" },
}) => {
    const volumeChipDownRef = useRef(undefined);
    const translate = useTranslation();

    const onClearCallback = () => {
        onChange?.({ fromValue: "", toValue: "" });
    };

    const doneSelected = (rangeValues: IRangeValues) => {
        onChange?.(rangeValues);
        allTrackers.dispatchTrackEvent("Filter Table", "Click", "DTS");
    };

    const logEvent = (isOpen: boolean, _isOutsideClick: boolean, _evt?: any) => {
        if (isOpen) {
            allTrackers.dispatchTrackEvent("Drop Down", "Click", "DTS");
        }
    };

    return (
        <PlainTooltip text={VOLUME_FILTER_DISABLED_TOOLTIP_KEY}>
            <ChipDownContainerWrapper onKeyDown={onContainerKeyDown(volumeChipDownRef)}>
                <ChipDownContainer
                    closeOnItemClick={false}
                    ref={volumeChipDownRef}
                    width={VOLUME_FILTER_WIDTH}
                    appendTo="#dts-filter"
                    dropdownPopupPlacement="ontop-left"
                    selectedText={getVolumeSelectedText(tableFilters, selectedText) ?? ""}
                    buttonText={translate(filterTextKey)}
                    onCloseItem={onClearCallback}
                    tooltipText={tooltipText}
                    noResultsMessage={translate("dropdown.no.results")}
                    enabledTooltip={enabledTooltip}
                    onToggle={logEvent}
                >
                    {[
                        <RangeFilter
                            key="range-filter"
                            onDoneCallback={doneSelected}
                            initialFromValue={tableFilters.fromValue}
                            initialToValue={tableFilters.toValue}
                            isValidRangeValues={isValidVolumeRangeValues}
                            onClearCallback={onClearCallback}
                            toPlaceHolder="100"
                            minValue={0}
                            maxValue={100}
                            {...rangeFilterProps}
                        />,
                    ]}
                </ChipDownContainer>
            </ChipDownContainerWrapper>
        </PlainTooltip>
    );
};
